import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";

import { AbcFitnessBrandLogo } from "@/components/portal/AbcFitnessBrandLogo";
import { GlofoxBrandLogo } from "@/components/portal/GlofoxBrandLogo";
import { usePageTitle } from "@/hooks/usePageTitle";
import { confirmAdminPasswordReset } from "@/lib/api/admin-reset-password";
import { parseApiError } from "@/lib/api/api-error";
import {
  getResetPasswordErrorMessage,
  isResetPasswordFormValid,
  RESET_PASSWORD_SUCCESS_MESSAGE,
  shouldOfferResetPasswordRequestLink,
  toResetPasswordRequest,
} from "@/lib/api/reset-password-form";
import { getAdminAuthSession } from "@/lib/auth/admin-session";

import "@/styles/admin-panel.css";

export default function AdminResetPasswordConfirmPage() {
  usePageTitle("Admin set new password");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  if (getAdminAuthSession()) {
    return <Navigate to="/admin/control-panel" replace />;
  }

  if (!token) {
    return <Navigate to="/admin/forgot-password" replace />;
  }

  const formValues = { password, confirmPassword };
  const canSubmit = isResetPasswordFormValid(formValues);
  const passwordsMismatch =
    confirmPassword.length > 0 && password !== confirmPassword;

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }

    setLoading(true);
    setError(null);
    setErrorCode(undefined);

    try {
      await confirmAdminPasswordReset(toResetPasswordRequest(token, formValues));
      navigate("/admin/login", {
        replace: true,
        state: { resetSuccess: RESET_PASSWORD_SUCCESS_MESSAGE },
      });
    } catch (err: unknown) {
      const parsed = parseApiError(err);
      setErrorCode(parsed.code);
      setError(getResetPasswordErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-login" data-testid="admin-reset-password-shell">
      <div className="admin-login-panel">
        <div className="admin-login-brand">
          <div className="admin-login-brand-row">
            <AbcFitnessBrandLogo
              to=""
              testId="admin-reset-password-abc-logo"
              theme="dark"
            />
            <span className="admin-login-sep" aria-hidden>
              /
            </span>
            <GlofoxBrandLogo to="" testId="admin-reset-password-glofox-logo" />
          </div>
          <div className="admin-login-product">API Admin Panel</div>
        </div>

        <div
          className="auth-card admin-login-card"
          data-testid="admin-reset-password-card"
        >
          <div className="auth-hdr admin-login-hdr">
            <p className="auth-eye">Password reset</p>
            <h2 data-testid="admin-reset-password-title">Choose a new password</h2>
            <p>Enter and confirm your new admin password below.</p>
          </div>
          <div className="auth-body">
            {error ? (
              <div
                className="error-msg"
                data-testid="admin-reset-password-error"
                role="alert"
              >
                <i className="ti ti-alert-circle" aria-hidden="true" />
                <span>
                  {error}{" "}
                  {shouldOfferResetPasswordRequestLink(errorCode, error) ? (
                    <Link
                      to="/admin/forgot-password"
                      data-testid="admin-reset-password-request-link"
                    >
                      Request a new link →
                    </Link>
                  ) : null}
                </span>
              </div>
            ) : null}

            <div className="field">
              <label htmlFor="admin-reset-password">New password</label>
              <div className="admin-login-pw">
                <input
                  id="admin-reset-password"
                  type={showPw ? "text" : "password"}
                  data-testid="admin-reset-password"
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
                <button
                  className="show-pw-btn"
                  data-testid="admin-reset-password-toggle"
                  onClick={() => setShowPw((v) => !v)}
                  type="button"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  aria-pressed={showPw}
                >
                  <i
                    className={`ti ${showPw ? "ti-eye-off" : "ti-eye"}`}
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>

            <div className="field">
              <label htmlFor="admin-reset-password-confirm">Confirm password</label>
              <input
                id="admin-reset-password-confirm"
                type={showPw ? "text" : "password"}
                data-testid="admin-reset-password-confirm"
                placeholder="Repeat your password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
              {passwordsMismatch ? (
                <p
                  data-testid="admin-reset-password-mismatch"
                  style={{ fontSize: 12, color: "#e24b4a", marginTop: 6 }}
                >
                  Passwords do not match.
                </p>
              ) : null}
            </div>

            <button
              type="button"
              className="submit-btn admin-login-submit"
              data-testid="admin-reset-password-submit"
              onClick={handleSubmit}
              disabled={loading || !canSubmit}
            >
              {loading ? (
                <>
                  <i
                    className="ti ti-loader-2 admin-login-spinner"
                    aria-hidden="true"
                  />{" "}
                  Updating…
                </>
              ) : (
                <>
                  <i className="ti ti-lock" aria-hidden="true" /> Update password
                </>
              )}
            </button>
          </div>
        </div>

        <p className="already" style={{ textAlign: "center", marginTop: "1rem" }}>
          <Link to="/admin/login" data-testid="admin-reset-password-login-link">
            Back to admin sign in →
          </Link>
        </p>
      </div>
    </div>
  );
}
